import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CognitoUser } from "amazon-cognito-identity-js";
import { Auth } from "aws-amplify";

// Types
import { getRank, Identity, Rank, Status, User } from "../types";

// State
import { setUsername } from "./signKeyFormSlice";
import { AppDispatch, AppState } from "./store";

interface SignedInUserState {
  status: Status;
  user: User | null;
}

const initialState: SignedInUserState = {
  user: null,
  status: Status.Idle,
};

type AsyncThunkConfig = {
  dispatch: AppDispatch;
};

const parseUser = (cognitoUser: CognitoUser): User => {
  const idpUsername = cognitoUser.getUsername();
  const payload = cognitoUser
    .getSignInUserSession()
    ?.getIdToken()
    ?.decodePayload();
  const groups = payload["cognito:groups"] || [];
  const identities: Identity[] = payload.identities || [];
  const { userId } = identities.find(({ primary }) => primary) || {};
  const username = userId || idpUsername;

  return {
    id: idpUsername,
    username,
    email: payload?.email ?? "",
    firstName: payload?.name ?? "",
    lastName: payload?.family_name ?? "",
    profiles: [],
    rank: getRank({ groups }),
  };
};

export const fetchUserAsync = createAsyncThunk<User, boolean, AsyncThunkConfig>(
  "signedInUser/fetch",
  async (bypassCache, thunkAPI): Promise<User> => {
    const cognitoUser: CognitoUser = await Auth.currentAuthenticatedUser({
      bypassCache,
    });

    const user = parseUser(cognitoUser);

    thunkAPI.dispatch(setUsername(user.username));

    return user;
  }
);

export const signedInUserSlice = createSlice({
  name: "signedInUser",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserAsync.pending, (state) => {
        state.status = Status.Loading;
      })
      .addCase(fetchUserAsync.rejected, (state) => {
        state.status = Status.Failed;
      })
      .addCase(
        fetchUserAsync.fulfilled,
        (state, { payload }: PayloadAction<User>) => {
          state.user = payload;
          state.status = Status.Idle;
        }
      );
  },
});

export const selectSignedInUser = (state: AppState): SignedInUserState =>
  state.signedInUser;

export const selectSignedInUserFullName = ({
  signedInUser: { user },
}: AppState): string =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ");

export const selectSignedInUserRank = (state: AppState): Rank =>
  state.signedInUser.user?.rank ?? Rank.User;

export default signedInUserSlice.reducer;
